import { Stack } from '@mui/material';
import { type Ref, useMemo } from 'react';

import { ARROW_EDGE_COLORS_BY_VARIANT } from '@/components/diagrams/FlowChart/edges/ArrowEdge';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';

import {
  CONNECTOR_OFFSET,
  COUPLE_NODE_HEIGHT,
  EX_SPOUSE_CONNECTOR_OVERLAP,
  EX_SPOUSE_CONNECTOR_WIDTH,
} from '../utils/elkLayout.constants';
import { PersonData } from './PersonTile';
import { SpousePersonTile } from './SpousePersonTile';

interface ExSpousesConnectorSvgProps {
  plural: boolean;
  position: 'left' | 'right';
}

function ExSpousesConnectorSvg({
  plural,
  position,
}: ExSpousesConnectorSvgProps) {
  const startX = useMemo(() => {
    if (!plural) {
      return EX_SPOUSE_CONNECTOR_OVERLAP * -1;
    }

    if (position === 'left') {
      return 0;
    }

    return EX_SPOUSE_CONNECTOR_OVERLAP * -1;
  }, [plural, position]);

  const endX = useMemo(() => {
    if (!plural) {
      return EX_SPOUSE_CONNECTOR_WIDTH + EX_SPOUSE_CONNECTOR_OVERLAP;
    }

    if (position === 'right') {
      return EX_SPOUSE_CONNECTOR_WIDTH;
    }

    return EX_SPOUSE_CONNECTOR_WIDTH + EX_SPOUSE_CONNECTOR_OVERLAP;
  }, [plural, position]);

  return (
    <div
      style={{
        position: 'relative',
        width: EX_SPOUSE_CONNECTOR_WIDTH,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <svg
        style={{
          position: 'absolute',
          top: 0,
          pointerEvents: 'none',
          zIndex: 1,
        }}
        width={EX_SPOUSE_CONNECTOR_WIDTH + EX_SPOUSE_CONNECTOR_OVERLAP * 2}
        height={COUPLE_NODE_HEIGHT}
        viewBox={`0 0 ${EX_SPOUSE_CONNECTOR_WIDTH} ${COUPLE_NODE_HEIGHT}`}
      >
        <line
          x1={startX.toString()}
          y1={CONNECTOR_OFFSET}
          x2={endX.toString()}
          y2={CONNECTOR_OFFSET}
          stroke={ARROW_EDGE_COLORS_BY_VARIANT.primary}
          strokeWidth="1"
          strokeDasharray="4 3"
        />
      </svg>
    </div>
  );
}

interface ExSpousesProps {
  id: string;
  exSpouses: PersonData[];
  position: 'left' | 'right';
  containerRef?: Ref<HTMLDivElement>;
  plural: boolean;
}

export function ExSpouses({
  id,
  exSpouses,
  position,
  containerRef,
  plural,
}: ExSpousesProps) {
  return (
    <>
      {/* Dashed svg between spouse and their ex spouses */}
      {position === 'right' && (
        <ExSpousesConnectorSvg plural={plural} position={position} />
      )}

      <div ref={containerRef} style={{ position: 'relative' }}>
        {/* Dashed rectangular border around ex-spouses */}
        {plural && (
          <>
            <svg
              style={{
                position: 'absolute',
                top: '-2px',
                left: '-2px',
                width: 'calc(100% + 4px)',
                height: 'calc(100% + 4px)',
                pointerEvents: 'none',
                zIndex: 0,
              }}
            >
              <rect
                x="1"
                y="1"
                width="calc(100% - 2px)"
                height="calc(100% - 2px)"
                fill="none"
                stroke={ARROW_EDGE_COLORS_BY_VARIANT.primary}
                strokeWidth="1"
                strokeDasharray="4 3"
                vectorEffect="non-scaling-stroke"
                rx="4"
              />
            </svg>
            <Badge
              variant={BadgeVariants.SecondaryOutline}
              display="Former relationships"
              sx={{ whiteSpace: 'nowrap' }}
              wrapperSx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                transform: 'translateY(-58%)',
                zoom: 0.75,
                transformOrigin: 'top center',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            />
          </>
        )}
        <Stack direction="row" style={{ position: 'relative', zIndex: 1 }}>
          {exSpouses.map((exSpouse) => (
            <SpousePersonTile key={exSpouse.id} id={id} person={exSpouse} />
          ))}
        </Stack>
      </div>

      {/* Dashed svg between spouse and their ex spouses */}
      {position === 'left' && (
        <ExSpousesConnectorSvg plural={plural} position={position} />
      )}
    </>
  );
}
